<template>
  <base-module
    :definition="saddleDefinitions"
    :use-active="true"
    :connected="true"
    :error="
      hasSaddleMotorError ||
      motorStatus.highTemperature ||
      motorStatus.hasHadHighTemperature
    "
    :inactive="hasOtherBlockingMotorError"
  >
    <template #title> {{ t('modules.saddle.index') }} </template>

    <template
      #left
      v-if="!hasSaddleMotorError"
    >
      <!-- Click events for fine grained control, hold events for smooth continues control-->
      <!-- Motor controls are increased by 2 to cover the time it takes to recieve a new position and thus create a smooth moving motor -->
      <motor-controls
        @click-up="
          updatePosition(saddleCurrentSetPointY, motorPosition.saddleY + 1)
        "
        @click-down="
          updatePosition(saddleCurrentSetPointY, motorPosition.saddleY - 1)
        "
        @click-left="
          updatePosition(saddleCurrentSetPointX, motorPosition.saddleX + 1)
        "
        @click-right="
          updatePosition(saddleCurrentSetPointX, motorPosition.saddleX - 1)
        "
        @hold-up="
          updatePosition(saddleCurrentSetPointY, motorPosition.saddleY + 5)
        "
        @hold-down="
          updatePosition(saddleCurrentSetPointY, motorPosition.saddleY - 5)
        "
        @hold-left="
          updatePosition(saddleCurrentSetPointX, motorPosition.saddleX + 5)
        "
        @hold-right="
          updatePosition(saddleCurrentSetPointX, motorPosition.saddleX - 5)
        "
      />
    </template>

    <template
      #right
      v-if="!hasSaddleMotorError"
    >
      <n-form
        ref="formRef"
        label-placement="left"
      >
        <n-form-item label="X">
          <n-input-group v-if="isCalibrated && isLatestMeasurementCalibrated">
            <n-input-number
              :value="currentPositionX"
              :show-button="false"
              @keyup.enter="currentPositionX = $event.target.value"
            />
            <n-input
              v-if="positionChange"
              :value="positionChange.saddleX"
              :show-button="false"
              disabled
              class="change-input"
            />
            <n-input-group-label>{{
              saddleCurrentPositionX.unit
            }}</n-input-group-label>
          </n-input-group>
          <n-input
            v-else
            :value="t('common.notCalibrated')"
            :show-button="false"
            disabled
          ></n-input>
        </n-form-item>

        <n-form-item label="Y">
          <n-input-group v-if="isCalibrated && isLatestMeasurementCalibrated">
            <n-input-number
              :value="currentPositionY"
              :show-button="false"
              @keyup.enter="currentPositionY = $event.target.value"
            />
            <n-input
              v-if="positionChange"
              :value="positionChange.saddleY"
              :show-button="false"
              disabled
              class="change-input"
            />
            <n-input-group-label>{{
              saddleCurrentPositionY.unit
            }}</n-input-group-label>
          </n-input-group>
          <n-input
            v-else
            :value="t('common.notCalibrated')"
            :show-button="false"
            disabled
          ></n-input>
        </n-form-item>
      </n-form>
    </template>

    <template
      #middle
      v-if="hasSaddleMotorError"
    >
      <MotorError :device="deviceType.saddle" />
    </template>
  </base-module>
</template>

<script setup lang="ts">
import {
  NInputNumber,
  NInput,
  NInputGroup,
  NInputGroupLabel,
  NForm,
  NFormItem,
  useMessage,
} from 'naive-ui'
import BaseModule from './BaseModule.vue'
import MotorControls from '@/components/controls/common/MotorControls.vue'
import MotorError from '@/components/controls/common/MotorError.vue'

import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { usePositionStore } from '@/store/positionStore'
import { useDeviceStore } from '@/store/deviceStore'
import { useI18n } from 'vue-i18n'
import { deviceType } from '@/types/device'
import { MqttValue } from '@/mqttDefinitions'

const { t } = useI18n()
const positionStore = usePositionStore()
const deviceStore = useDeviceStore()
const message = useMessage()

import {
  saddleDefinitions,
  saddleCurrentPositionX,
  saddleCurrentPositionY,
  saddleCurrentSetPointX,
  saddleCurrentSetPointY,
} from '@/mqttDefinitions'

const { submitPosition } = positionStore
const { absolutePosition, positionOffset, motorPosition, positionChange } =
  storeToRefs(positionStore)
const { isCalibrated, isLatestMeasurementCalibrated } =
  storeToRefs(positionStore)
const { motorStatus } = storeToRefs(deviceStore)

const updatePosition = (definition: MqttValue, newValue: number) => {
  if (!submitPosition(definition, newValue)) {
    message.error(t('setup.calibrate.outOfReach'))
  }
}

const currentPositionX = computed({
  get() {
    return absolutePosition.value.saddleX
  },
  set(newValue) {
    updatePosition(
      saddleCurrentSetPointX,
      newValue - positionOffset.value.saddleX,
    )
  },
})

const currentPositionY = computed({
  get() {
    return absolutePosition.value.saddleY
  },
  set(newValue) {
    updatePosition(
      saddleCurrentSetPointY,
      newValue - positionOffset.value.saddleY,
    )
  },
})

const hasSaddleMotorError = computed(() => {
  return (
    motorStatus.value.saddle.x.overcurrent ||
    motorStatus.value.saddle.y.overcurrent ||
    motorStatus.value.saddle.x.stalled ||
    motorStatus.value.saddle.y.stalled ||
    motorStatus.value.saddle.x.limitSwitch ||
    motorStatus.value.saddle.y.limitSwitch ||
    motorStatus.value.highTemperature ||
    motorStatus.value.hasHadHighTemperature
  )
})

const hasOtherBlockingMotorError = computed(() => {
  return (
    motorStatus.value.handleBar.x.overcurrent ||
    motorStatus.value.handleBar.y.overcurrent ||
    motorStatus.value.handleBar.x.stalled ||
    motorStatus.value.handleBar.y.stalled ||
    motorStatus.value.handleBar.x.limitSwitch ||
    motorStatus.value.handleBar.y.limitSwitch
  )
})
</script>

<style lang="scss" scoped>
@media only screen and (max-width: 500px) {
  .n-input.change-input {
    display: none;
  }
}
</style>
